import React, { useEffect, useState } from 'react';
import { Heart, ShoppingBag, Check } from 'lucide-react';
import { useApp } from '../../context/AppContext';
import ProductDetail from '../../components/ProductDetail';
import { formatPrice } from '../../utils/price';
import { hasDiscount, priceBefore } from '../../utils/discount';

export default function Favorites() {
  const { favorites, toggleFavorite, addToCart, cart, refreshFavorites, clearFavorites } = useApp();
  const [showDetail, setShowDetail] = useState(false);
  const [detailProduct, setDetailProduct] = useState(null);

  useEffect(() => {
    refreshFavorites();
  }, []);

  return (
    <div className="pb-4">
      <header className="sticky top-0 z-30 bg-cream/85 px-4 pb-3 pt-4 backdrop-blur-xl">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="grid h-9 w-9 place-items-center rounded-xl bg-terracotta text-white">
              <Heart className="h-5 w-5" />
            </div>
            <div>
              <div className="text-[11px] uppercase tracking-widest text-green-muted">Мои избранные</div>
              <div className="-mt-0.5 text-base font-bold text-green-dark">
                Избранное
                <span className="ml-2 text-sm font-normal text-green-muted">· {favorites.length}</span>
              </div>
            </div>
          </div>
          {favorites.length > 0 && (
            <button onClick={clearFavorites} className="text-sm text-green-muted hover:text-[#D64545]">Очистить</button>
          )}
        </div>
      </header>
      <div className="px-4">
        {favorites.length > 0 ? (
          <div className="space-y-3">
            {favorites.map(product => {
              const inCart = cart.find(item => item.id === product.id);
              const quantity = inCart ? inCart.quantity : 0;
              const isOutOfStock = !product.in_stock || product.quantity <= 0;
              const hasVariants = (product.variants || []).length > 0;

              return (
                <div key={product.id} className="flex items-center gap-3 rounded-2xl bg-cream-card p-3 ring-1 ring-border-warm">
                  <div className="h-16 w-16 flex-shrink-0 rounded-xl bg-cream flex items-center justify-center text-2xl overflow-hidden">
                    {product.image_url ? (
                      <img src={product.image_url} alt={product.name} className="h-full w-full object-cover rounded-xl" />
                    ) : '🌱'}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-sm font-medium text-green-dark truncate">{product.name}</h3>
                    {product.price > 0 ? (
                      <div className="flex flex-wrap items-center gap-1.5">
                        {hasDiscount(product) && (
                          <>
                            <span className="text-xs text-green-muted line-through">
                              {formatPrice(priceBefore(product.price, product.discount_percent))} ₽
                            </span>
                            <span className="rounded-full bg-[#D64545]/15 px-1.5 py-0.5 text-[10px] font-semibold text-[#D64545]">
                              −{product.discount_percent}%
                            </span>
                          </>
                        )}
                        <p className="text-sm font-bold text-green-dark">
                          {(product.variants || []).length > 0 ? `от ${formatPrice(product.price)}` : formatPrice(product.price)} ₽
                        </p>
                      </div>
                    ) : (
                      <p className="text-xs text-green-muted">Нет в наличии</p>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    {isOutOfStock ? null : hasVariants ? (
                      <button
                        onClick={() => { setDetailProduct(product); setShowDetail(true); }}
                        className="grid h-9 w-9 place-items-center rounded-xl bg-terracotta text-white active:scale-90"
                        aria-label="Выбрать размер"
                      >
                        <ShoppingBag className="h-4 w-4" />
                      </button>
                    ) : quantity === 0 ? (
                      <button
                        onClick={() => addToCart(product, 1)}
                        className="grid h-9 w-9 place-items-center rounded-xl bg-terracotta text-white active:scale-90"
                      >
                        <ShoppingBag className="h-4 w-4" />
                      </button>
                    ) : (
                      <div className="flex items-center gap-1.5 rounded-xl bg-green-sage/20 px-3 py-2">
                        <Check className="h-4 w-4 text-green-primary" />
                        <span className="text-xs font-semibold text-green-primary">В корзине</span>
                      </div>
                    )}
                    <button
                      onClick={() => toggleFavorite(product)}
                      className="grid h-9 w-9 place-items-center rounded-full text-rose-500 active:scale-90"
                    >
                      <Heart className="h-5 w-5 fill-rose-500" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="rounded-2xl bg-cream-card py-16 text-center text-sm text-green-muted">
            <Heart className="mx-auto mb-2 h-8 w-8" />
            <p>Нет избранных товаров</p>
            <p className="mt-1 text-xs">Добавьте товары в избранное из каталога</p>
          </div>
        )}
      </div>

      {showDetail && detailProduct && (
        <ProductDetail
          product={detailProduct}
          onClose={() => { setShowDetail(false); setDetailProduct(null); }}
        />
      )}
    </div>
  );
}
